import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { getCancellationPreEligibility } from '@acorn-portal/shared/src/store/cancellation/cancellation.slice';
import { useEffect } from 'react';
import { useCancellationsPreRequisites } from './use-cancellation-pre-requisites';

/**
 * Hook that automatically fetches pre-eligibility results from the Cancellations API.
 *
 * Pre-eligibility is fetched under the following conditions:
 * - Pre-requisites are met (see `useCancellationsPreRequisites`)
 * - Pre-eligibility hasn't been checked yet without error
 *
 * This determines lightweight visibility of cancellation access points (on page load) before the user
 * initiates the full eligibility check imperatively.
 *
 * @returns An object containing the loading state and pre-eligibility result.
 */
export const useCancellationPreEligibilityCheck = () => {
	const preRequisitesPass = useCancellationsPreRequisites();
	const cancellationState = useAppSelector((state) => state.cancellation);
	const isLoading = cancellationState.preEligibilityStatus.isLoading;
	const checkedPreEligibility = cancellationState.checkedPreEligibility;

	const dispatch = useAppDispatch();

	useEffect(() => {
		// Skip check if:
		// - Pre-requisites are not met
		// - Pre-eligibility have already been checked (they won't check in the event of an error - see cancellation.slice > getCancellationPreEligibility.rejected)
		if (preRequisitesPass && !checkedPreEligibility) {
			dispatch(getCancellationPreEligibility());
		}
	}, [preRequisitesPass, checkedPreEligibility, dispatch]);

	return {
		isLoading: isLoading,
		isPreEligible: cancellationState.isPreEligible,
	};
};
